import { ProductImage } from "./productImage";
import type { PaymentMethod } from "../utils/paymentMethods";

// API Response Types
export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message: string;
    timestamp: string;
}

export interface ApiError {
    detail: string;
    type?: string;
    loc?: string[];
}

// Authentication Types
export type UserRole = "OWNER" | "MANAGER" | "SHOP_ASSISTANT" | "INVESTOR";

export const USER_ROLE_LABELS: Record<UserRole, string> = {
    OWNER: "Owner",
    MANAGER: "Manager",
    SHOP_ASSISTANT: "Shop Assistant",
    INVESTOR: "Investor",
};

export interface User {
    user_id: string;
    email: string;
    is_approved: boolean;
    role: UserRole;
    /** Optional name shown on tasks and avatars. */
    display_name?: string | null;
    create_time: string;
    update_time: string;
}

export interface LoginRequest {
    username: string;
    password: string;
}

export interface RegisterRequest {
    email: string;
    password: string;
}

export interface AuthResponse {
    token_type: string;
    access_token: string;
    expires_at: number;
    refresh_token: string;
    refresh_token_expires_at: number;
}

export interface RefreshTokenRequest {
    refresh_token: string;
}

// Product Types
export interface Category {
    category_id: string;
    name: string;
    slug?: string;
    description?: string;
    parent_id?: string;
    is_active: boolean;
    is_featured?: boolean;
    display_order?: number;
    image_url?: string;
    level?: number;
    path?: string;
    create_time: string;
    update_time: string;
}

export interface CategoryWithChildren extends Category {
    children: CategoryWithChildren[];
    product_count: number;
}

export interface CategoryBreadcrumb {
    category_id: string;
    name: string;
    slug: string;
    level: number;
}

export interface Brand {
    brand_id: string;
    name: string;
    description?: string;
    is_active: boolean;
    is_featured?: boolean;
    display_order?: number;
    image_url?: string;
    /** Brand page address on the shop: /shop?brand=<slug>. */
    slug: string;
    /** Wide banner at the top of the brand page. */
    cover_image_url?: string | null;
    create_time: string;
    update_time: string;
}

export interface ProductVariant {
    variant_id: string;
    name: string;
    sku: string;
    price: number;
    cost_price: number;
    weight?: number;
    is_active: boolean;
}

export interface StockInfo {
    quantity: number;
    reserved_quantity: number;
    min_stock_level: number;
    max_stock_level: number;
    last_updated?: string; // Optional for backward compatibility
}

/** A combo price: from `min_quantity` units, each costs `unit_price`. */
export interface PriceTier {
    min_quantity: number;
    unit_price: number;
}

export interface Product {
    product_id: string;
    name: string;
    /** Its web address on the shop: /products/<slug>. */
    slug?: string;
    description?: string;
    category_id: string;
    brand_id?: string;
    selling_price: number;
    cost_price: number;
    // Flash-sale / discount pricing. A plain ongoing discount is sale_price
    // set with both dates left null/undefined. is_on_sale/effective_price/
    // discount_percent are computed server-side (Product.is_on_sale et al)
    // and echoed back on every read — never re-derive the date-window
    // logic client-side, to avoid disagreeing with the server's clock.
    sale_price?: number | null;
    sale_starts_at?: string | null;
    sale_ends_at?: string | null;
    is_on_sale?: boolean;
    effective_price?: number;
    discount_percent?: number | null;
    sku: string;
    barcode?: string;
    weight?: number;
    gross_weight?: number;
    dimensions?: string;
    is_active: boolean;
    is_featured?: boolean;
    is_visible: boolean;
    is_merged?: boolean;
    /** Sunk below the website's automatic order (Arrange Shop). */
    is_pushed_down?: boolean;
    /** A campaign's price — a layer beside the product's own sale above;
     * the lower live one is effective_price. Set only by campaigns. */
    campaign_price?: number | null;
    campaign_price_starts_at?: string | null;
    campaign_price_ends_at?: string | null;
    /** The campaign that set campaign_price. */
    sale_campaign_id?: string | null;
    /** The live campaign it's part of, if any. */
    live_campaign?: { name: string; slug: string; badge_label: string; ends_at: string | null } | null;
    /** Combo prices, smallest quantity first — see utils/productPricing. */
    price_tiers?: PriceTier[];
    merged_into_product_id?: string;
    create_time: string;
    update_time: string;
    category?: Pick<Category, "category_id" | "name">;
    categories?: Category[];
    brand?: Pick<Brand, "brand_id" | "name">;
    variants?: ProductVariant[];
    stock?: StockInfo;
    images?: ProductImage[];
    // Who created/last edited this product — resolve via userService.getUserBasic()
    created_by_user_id?: string;
    updated_by_user_id?: string;
}

export interface CreateProductRequest {
    name: string;
    /** The web address; made from the name when left out. */
    slug?: string;
    description?: string;
    category_id: string;
    brand_id?: string;
    selling_price: number;
    cost_price: number;
    // null (not undefined) explicitly clears an existing discount — see
    // ProductList.tsx's Remove Discount bulk action.
    sale_price?: number | null;
    sale_starts_at?: string | null;
    sale_ends_at?: string | null;
    sku: string;
    barcode?: string;
    weight?: number;
    gross_weight?: number;
    dimensions?: string;
    is_active: boolean;
    is_featured?: boolean;
    is_visible?: boolean;
    // Initial stock settings
    initial_stock?: number;
    min_stock_level?: number;
    max_stock_level?: number;
    // Multi-category support
    category_ids?: string[];
}

// Stock Types
export interface Stock {
    stock_id: string;
    product_id: string;
    available_quantity: number;
    reserved_quantity: number;
    reorder_level: number;
    max_stock_level: number;
    location?: string;
    is_low_stock: boolean;
    create_time: string;
    update_time: string;
}

export interface StockMovement {
    movement_id: string;
    stock_id: string;
    reason:
        | "PURCHASE"
        | "SALE"
        | "ADJUSTMENT"
        | "RETURN"
        | "DAMAGE"
        | "EXPIRED";
    quantity: number;
    reference_type?: string;
    reference_id?: string;
    notes?: string;
    create_time: string;
    update_time: string;
}

// Customer Types
export interface CustomerAddress {
    address_id: string;
    customer_id: string;
    address_type: "BILLING" | "SHIPPING";
    street_address: string;
    city: string;
    map_link?: string;
    is_default: boolean;
    create_time: string;
    update_time: string;
}

export interface Pet {
    pet_profile_id: string;
    customer_id: string;
    name: string;
    species: string;
    breed?: string;
    birthday?: string;
    gender?: "male" | "female" | null;
    /** Decimal from the API — arrives as a string, e.g. "4.20". */
    weight_kg?: string | number | null;
    /** Food preferences, allergies, anything staff should know. */
    notes?: string | null;
    photo_url?: string;
    create_time: string;
    update_time: string;
}

/** What a pet create/update sends — null clears an optional field. */
export interface PetPayload {
    name?: string;
    species?: string;
    breed?: string | null;
    birthday?: string | null;
    gender?: "male" | "female" | null;
    weight_kg?: number | null;
    notes?: string | null;
}

export interface Customer {
    customer_id: string;
    first_name: string;
    last_name?: string;
    email?: string;
    phone?: string;
    alternative_phone?: string;
    date_of_birth?: string;
    profile?: string;
    source: "ADMIN" | "ECOMMERCE" | "MERGED";
    status: "ACTIVE" | "INACTIVE" | "DUPLICATE" | "MERGED";
    master_customer_id?: string;
    is_email_verified: boolean;
    is_phone_verified: boolean;
    preferred_contact_method?: "EMAIL" | "PHONE" | "SMS";
    marketing_consent: boolean;
    newsletter_subscription: boolean;
    note?: string;
    last_login?: string;
    last_order_date?: string;
    create_time: string;
    update_time: string;
    addresses?: CustomerAddress[];
    pets?: Pet[];
    // Computed properties
    full_name?: string;
    is_duplicate?: boolean;
    is_merged?: boolean;
    is_active?: boolean;
    can_login?: boolean;
}

export interface CreateCustomerRequest {
    first_name: string;
    last_name?: string;
    email?: string;
    phone?: string;
    alternative_phone?: string;
    date_of_birth?: string;
    profile?: string;
    is_active: boolean;
    marketing_consent?: boolean;
    newsletter_subscription?: boolean;
    preferred_contact_method?: "EMAIL" | "PHONE" | "SMS";
    note?: string;
    addresses?: Omit<
        CustomerAddress,
        "address_id" | "customer_id" | "create_time" | "update_time"
    >[];
}

export interface UpdateCustomerRequest {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    alternative_phone?: string;
    date_of_birth?: string;
    profile?: string;
    is_active?: boolean;
    marketing_consent?: boolean;
    newsletter_subscription?: boolean;
    preferred_contact_method?: "EMAIL" | "PHONE" | "SMS";
    note?: string;
    addresses?: Omit<
        CustomerAddress,
        "address_id" | "customer_id" | "create_time" | "update_time"
    >[];
}

// Customer Merge Types
export interface DuplicateCandidate {
    candidate_id: string;
    customer1_id: string;
    customer2_id: string;
    match_score: number;
    match_criteria: {
        email_match?: boolean;
        phone_match?: boolean;
        name_similarity: number;
        first_name_similarity: number;
        last_name_similarity: number;
        dob_match?: boolean;
        total_score: number;
    };
    status: "PENDING" | "REVIEWED" | "MERGED" | "DISMISSED";
    reviewed_by_user_id?: string;
    reviewed_date?: string;
    notes?: string;
    create_time: string;
}

export interface CustomerMergeRequest {
    master_customer_id: string;
    duplicate_customer_id: string;
    merge_reason?: string;
}

export interface MergeLog {
    merge_id: string;
    master_customer_id: string;
    merged_customer_id: string;
    merged_customer_data: any;
    merge_reason?: string;
    merged_by_user_id?: string;
    merge_date: string;
}

// Order Types
export interface OrderItem {
    item_id: string;
    order_id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
    unit_cost_price?: number;
    // Product.selling_price snapshotted when this item was added — undefined
    // for items created before this field existed. Show a "was" price only
    // when it's present AND greater than unit_price (covers a manual
    // discount too, not only a flash sale) — never treat it as "no
    // discount" just because it's missing.
    list_price?: number;
    total_price: number;
    is_gift?: boolean;
    create_time: string;
    update_time: string;
    product?: Pick<
        Product,
        "product_id" | "name" | "sku" | "selling_price" | "images"
    >;
}

// Payment Types — canonical list lives in utils/paymentMethods.ts (the type
// is derived there from the one value list, not hand-declared twice).
// Re-exported here so existing `import { PaymentMethod } from "../types"`
// call sites across the app keep working unchanged.
export type { PaymentMethod };

export type PaymentStatus = "UNPAID" | "PARTIAL" | "PAID" | "OVERPAID" | "REFUNDED";

export interface PaymentTransaction {
    transaction_id: string;
    order_id: string;
    amount: number;
    payment_method: PaymentMethod;
    payment_date: string;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
    // Set only for a real bKash-gateway row (payment or refund) — never
    // for a manually entered "BKASH"-labeled payment. Gates the Refund
    // action and hides the Delete action, since payment_method alone
    // can't tell a gateway payment from a staff typing "BKASH" by hand.
    bkash_payment_id?: string | null;
    is_refund: boolean;
    original_transaction_id?: string | null;
    // Set when this transaction is the refund issued for an OrderReturn —
    // see orderReturnService.ts.
    return_id?: string | null;
    created_by_user_id?: string;
    create_time: string;
    update_time: string;
}

export interface PaymentSummary {
    total_amount: number;
    amount_paid: number;
    amount_due: number;
    payment_status: PaymentStatus;
    transaction_count: number;
    transactions: PaymentTransaction[];
}

export interface Order {
    order_id: string;
    order_number: string;
    customer_id: string;
    // Flat customer name/email, as returned by the order search endpoint
    // (as opposed to the nested `customer` object below, which other
    // endpoints populate instead).
    customer_name?: string;
    customer_email?: string;
    status: "DRAFT" | "PENDING" | "CONFIRMED" | "SHIPPED" | "DELIVERED" | "CANCELLED";
    order_date: string;
    total_amount: number;
    discount_amount?: number;
    shipping_amount?: number;
    delivery_cost?: number;
    total_cost_price?: number;
    /** The delivery area the charge was for; its name is kept on the order. */
    delivery_area_id?: string | null;
    delivery_area_name?: string | null;
    shipping_address: string;
    // Write-only: set when shipping_address was populated by picking one of
    // the customer's saved addresses rather than typed freehand. On a
    // successful save, the backend promotes that address to the customer's
    // default — see OrderService._promote_default_address. Never present
    // on a fetched Order (API responses don't echo it back).
    customer_address_id?: string;
    delivery_service_id?: string;
    notes?: string;
    shipped_date?: string;
    delivered_date?: string;
    create_time: string;
    update_time: string;

    // Payment fields
    payment_status: PaymentStatus;
    amount_paid: number;
    amount_due: number;

    customer?: Pick<
        Customer,
        "customer_id" | "first_name" | "last_name" | "email" | "phone"
    >;
    items?: OrderItem[];
    payment_transactions?: PaymentTransaction[];
    delivery_service?: {
        delivery_service_id: string;
        name: string;
        type: string;
        tracking_url_template?: string;
        label_url_template?: string;
        tg_chat_id?: string;
    };
    delivery_service_name?: string;
    delivery_service_type?: string;
    consignment_id?: string;
    order_channel_id?: string;
    order_channel?: {
        order_channel_id: string;
        name: string;
        type: "OFFLINE" | "ONLINE";
    };
    order_channel_name?: string;
    order_channel_type?: string;

    // Whether gift items (OrderItem.is_gift) print on the customer-facing bill
    show_gifts_on_bill?: boolean;

    // Who created/last edited this order — resolve via userService.getUserBasic().
    // May be undefined (e.g. delivery-webhook updates have no staff member to attribute).
    created_by_user_id?: string;
    updated_by_user_id?: string;
}

export interface CreateOrderRequest {
    customer_id?: string;
    status: Order["status"];
    total_amount: number;
    discount_amount?: number;
    shipping_amount?: number;
    delivery_cost?: number;
    shipping_address: string;
    // See Order.customer_address_id above.
    customer_address_id?: string;
    delivery_area_id?: string;
    delivery_service_id?: string;
    order_channel_id?: string;
    notes?: string;
    show_gifts_on_bill?: boolean;
    items: {
        product_id: string;
        quantity: number;
        unit_price: number;
        total_price: number;
        is_gift?: boolean;
    }[];
    payments?: {
        amount: number;
        payment_method: PaymentMethod;
        reference_number?: string;
        notes?: string;
        account_id?: string | null;
    }[];
    allow_negative_stock?: boolean;
}

export interface OrderSummary {
    total_orders: number;
    total_revenue: number;
    average_order_value: number;
    orders_by_status: Record<Order["status"], number>;
    top_products: {
        product_id: string;
        name: string;
        quantity_sold: number;
        revenue: number;
    }[];
}

export interface OrderSearchParams {
    // Text search
    search_query?: string;

    // Filters
    status?: Order["status"];
    payment_status?: PaymentStatus;
    customer_id?: string;
    customer_ids?: string[];
    created_by_user_id?: string;
    product_id?: string;
    product_ids?: string[];

    // Order channel — specific channel takes precedence over type if both are set
    order_channel_id?: string;
    order_channel_type?: "OFFLINE" | "ONLINE";
    // Opt-in: when filtering by order_channel_type, also include orders
    // with no channel recorded at all (e.g. a counter sale logged without
    // picking a channel) rather than silently excluding them.
    include_unassigned_channel?: boolean;
    // Opt-in: exclude any order that already has a delivery_service_id.
    // Pair with include_unassigned_channel so a delivery order that
    // happens to lack a channel tag isn't double-counted as a counter
    // sale too — it's already covered by a delivery_service_type search.
    exclude_has_delivery_service?: boolean;

    // Delivery service — specific service takes precedence over type if both are set
    delivery_service_id?: string;
    delivery_service_type?: "INHOUSE" | "THIRD_PARTY";

    // true = only orders with a gift item, false = only orders with none
    has_gift_items?: boolean;

    // Date ranges
    order_date_from?: string;
    order_date_to?: string;
    shipped_date_from?: string;
    shipped_date_to?: string;
    delivered_date_from?: string;
    delivered_date_to?: string;

    // Amount ranges
    total_amount_min?: number;
    total_amount_max?: number;

    // Opt-in: embed each order's itemized payment_transactions in the
    // response. Off by default — only turn on when you actually need a
    // payment-method breakdown, since it adds payload most callers don't need.
    include_payments?: boolean;

    // Sorting
    sort_by?:
        | "order_date"
        | "total_amount"
        | "order_number"
        | "status"
        | "customer_name";
    sort_order?: "asc" | "desc";

    // Pagination
    skip?: number;
    limit?: number;
}

// Common Types
export interface PaginationParams {
    skip?: number;
    limit?: number;
}

export interface CustomerListResponse {
    customers: Customer[];
    total: number;
    page: number;
    per_page: number;
}

export interface PaginatedResponse<T> {
    items: T[];
    data: T[];
    total: number;
    skip: number;
    limit: number;
}

export interface SearchParams extends PaginationParams {
    search?: string;
    active_only?: boolean;
}

export interface OrderSearchResponse {
    items: Order[];
    total: number;
    skip: number;
    limit: number;
    search_params?: any;
}

export interface StatItem {
    label: string;
    value: number;
    count?: number;
    percentage?: number;
}

export interface TopEntityStat {
    id: string;
    name: string;
    count: number;
    amount: number;
}

export interface DeliveryServiceStat {
    name: string;
    type: "INHOUSE" | "THIRD_PARTY" | null;
    count: number;
    revenue: number;
    cost: number;
    profit: number;
    profit_margin: number;
}

export interface OrderStatisticsResponse {
    // Overview
    total_orders: number;
    total_amount: number;
    average_order_value: number;

    // Breakdowns
    status_breakdown: StatItem[];
    channel_breakdown: StatItem[];
    delivery_service_breakdown: DeliveryServiceStat[];

    // Top Performers
    top_products: TopEntityStat[];
    top_customers: TopEntityStat[];

    // Financials
    total_shipping: number;
    total_delivery_cost: number;
    total_discount: number;
    total_tax: number;

    // Gift items (given free — cost of goods given away, not reflected in discount)
    total_gift_cost: number;
    gift_items_count: number;

    // Payment Statistics
    total_paid: number;
    total_due: number;
    payment_status_breakdown: StatItem[];
    payment_methods_breakdown: StatItem[];

    // Delivery Statistics (revenue vs cost)
    delivery_stats: {
        revenue: number; // What we charge customers (shipping_amount)
        cost: number; // What we pay to delivery partners (delivery_cost)
        profit: number; // Net profit from delivery
        profit_margin: number; // Profit margin percentage
    };
}

export interface PaginatedStatsResponse {
    items: TopEntityStat[];
    total: number;
    skip: number;
    limit: number;
}

export interface DashboardAnalyticsParams extends PaginationParams {
    search?: string;
    active_only?: boolean;
    preset?:
        | "today"
        | "yesterday"
        | "this_week"
        | "last_week"
        | "this_month"
        | "last_month"
        | "this_year"
        | "last_year"
        | "all_time"
        | "custom";
    start_date?: string;
    end_date?: string;
    include_comparison?: boolean;
    include_daily_breakdown?: boolean;
}

// Doubles as the Products page's Advanced Search state — extended in
// place (rather than a parallel "ProductSearchParams" type) so the list
// fetch, the search dialog, and CSV export all share one definition of
// "what a product filter is" and can never drift out of sync with each
// other. Mirrors the shape/spirit of OrderSearchParams (types/index.ts
// ~504-566): tri-state booleans are `boolean | undefined` (undefined =
// "all"), ranges are paired min/max or _from/_to fields.
export interface GetProductsParams extends SearchParams {
    featured_only?: boolean;
    category_id?: string;
    include_stock?: boolean;
    brand_id?: string;

    // Tri-state overrides — undefined leaves active_only/featured_only's
    // own default behavior in place; set explicitly to see "X only" or
    // "NOT X" (e.g. is_active: false surfaces inactive products, which
    // active_only alone can never do since it only ever adds an
    // is-active-true condition).
    is_active?: boolean;
    is_visible?: boolean;
    is_featured?: boolean;

    min_price?: number;
    max_price?: number;
    min_cost_price?: number;
    max_cost_price?: number;
    on_sale_only?: boolean;

    stock_status?: "in_stock" | "low_stock" | "out_of_stock";

    created_from?: string;
    created_to?: string;

    sort_by?:
        | "featured"
        | "price-asc"
        | "price-desc"
        | "name-asc"
        | "name-desc"
        | "newest";

    /** Leave out product/category/brand descriptions (list views only —
     * open a product with getProductById for the full record). */
    lean?: boolean;
}

// Form Types
export interface FormField {
    name: string;
    label: string;
    type:
        | "text"
        | "email"
        | "password"
        | "number"
        | "select"
        | "textarea"
        | "date"
        | "checkbox";
    required?: boolean;
    options?: { value: string | number; label: string }[];
    multiline?: boolean;
    rows?: number;
}

// Navigation Types
export interface NavItem {
    id: string;
    label: string;
    path: string;
    icon: React.ComponentType;
    children?: NavItem[];
    badgeKey?: string;
}

// Supplier Types
export interface Supplier {
    supplier_id: string;
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    notes?: string;
    is_active: boolean;
    create_time: string;
    update_time: string;
}

export interface CreateSupplierRequest {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    notes?: string;
}

// Table Types
export interface TableColumn {
    field: string;
    headerName: string;
    width?: number;
    flex?: number;
    type?: "string" | "number" | "date" | "boolean" | "actions";
    renderCell?: (params: any) => React.ReactNode;
    valueGetter?: (params: any) => any;
}

// Courier Service Types (Steadfast API)
export interface SteadfastCourierCredentials {
    apiKey: string;
    secretKey: string;
}

export interface CreateCourierOrderRequest {
    invoice: string;
    recipient_name: string;
    recipient_phone: string;
    alternative_phone?: string;
    recipient_email?: string;
    recipient_address: string;
    cod_amount: number;
    note?: string;
    item_description?: string;
    total_lot?: number;
    delivery_type?: 0 | 1; // 0 = Home delivery, 1 = Point Delivery/Hub pick up
}

export interface SteadfastCourierResponse {
    status: number;
    message: string;
    consignment: {
        consignment_id: number;
        invoice: string;
        tracking_code: string;
        recipient_name: string;
        recipient_phone: string;
        recipient_address: string;
        cod_amount: number;
        status: string;
        note?: string;
        created_at: string;
        updated_at: string;
    };
}

export interface CourierOrderMapping {
    order_id: string;
    consignment_id?: number;
    tracking_code?: string;
    status: string;
    created_at?: string;
    updated_at?: string;
}

// Expense Management Types
export interface ExpenseCategory {
    category_id: string;
    name: string;
    description?: string;
    is_active: boolean;
    create_time: string;
    update_time: string;
}

export interface ExpenseCategoryCreate {
    name: string;
    description?: string;
    is_active?: boolean;
}

export interface ExpenseCategoryUpdate {
    name?: string;
    description?: string;
    is_active?: boolean;
}

export interface Expense {
    expense_id: string;
    category: ExpenseCategory;
    amount: number;
    expense_date: string;
    payment_method: PaymentMethod;
    vendor_name?: string;
    reference_number?: string;
    notes?: string;
    account_id?: string;
    created_by_user_id?: string;
    create_time: string;
    update_time: string;
}

export interface ExpenseCreate {
    category_id: string;
    amount: number;
    expense_date: string;
    payment_method: PaymentMethod;
    vendor_name?: string;
    reference_number?: string;
    notes?: string;
    account_id?: string | null;
}

export interface ExpenseUpdate {
    category_id?: string;
    amount?: number;
    expense_date?: string;
    payment_method?: PaymentMethod;
    vendor_name?: string;
    reference_number?: string;
    notes?: string;
    // null explicitly untags; omitting the key leaves the current tag
    // alone — see ExpenseForm's use of AccountPicker, which always emits
    // one of those two, never `undefined`.
    account_id?: string | null;
}

export interface ExpenseSearchParams {
    search_query?: string;
    category_ids?: string[];
    payment_method?: PaymentMethod;
    expense_date_from?: string;
    expense_date_to?: string;
    amount_min?: number;
    amount_max?: number;
    vendor_name?: string;
}

export interface ExpenseStatItem {
    label: string;
    value: number;
    count: number;
    percentage: number;
}

export interface ExpenseStatisticsResponse {
    total_expenses: number;
    total_amount: number;
    average_expense: number;
    category_breakdown: ExpenseStatItem[];
    payment_method_breakdown: ExpenseStatItem[];
    monthly_breakdown?: Array<{
        year: number;
        month: number;
        count: number;
        amount: number;
    }>;
}

// Re-export analytics types
export * from "./analytics";
